import { SPOTLIGHT_TARGETS } from "../spotlightTargets";
import { SPOTLIGHT_EVENTS } from "../spotlightEventNames";
import { COMPLETE_CHAPTER } from "../tutorialEngine";
import { buildWidgetSteps, WIDGETS_INTRO_STEP_ID, WIDGETS_COMPLETE_STEP_ID } from "./announcementWidgetSteps";

const ID = {
	FRIENDS: "friends",
	SETTINGS: "settings",
	VIEW_AS_VISITOR: "view-as-visitor",
	EDIT_PAGE_SETTINGS: "edit-page-settings",
	GRADUATION_CARD: "graduation-card",
	GRADUATION_FUND: "graduation-fund",
	REGISTRY_PREVIEW: "registry-preview",
	SHARE: "share",
	REGISTRY: "registry",
};

/** Steps inside a modal: they disappear with it, and closing the modal early jumps to `exitStepId`. */
const insideModal = (exitStepId, step) => ({ allowBack: false, completeWhenTargetGone: exitStepId, ...step });

const friendsModalSteps = [
	{
		id: "friends-modal",
		target: SPOTLIGHT_TARGETS.FRIENDS_MODAL,
		title: "Your friends",
		body: "This is where you manage your friends and connect with other MyTassel users, from classmates to family.",
	},
	{
		id: "friends-search",
		target: SPOTLIGHT_TARGETS.FRIENDS_SEARCH,
		title: "Find people",
		body: "Search by name, UID, institution name, or institution code to find people to connect with.",
	},
	{
		id: "friends-tabs",
		target: SPOTLIGHT_TARGETS.FRIENDS_TABS,
		title: "Friends tabs",
		body: "Switch between the lists:",
		bullets: [
			{ label: "Suggested", text: "people you may know." },
			{ label: "Following / Followers", text: "who you follow and who follows you." },
			{ label: "Requests", text: "friend requests waiting for you." },
		],
	},
	{
		id: "friends-privacy",
		target: SPOTLIGHT_TARGETS.FRIENDS_PRIVACY,
		title: "Public or private",
		body: "This switch controls whether your account is public or private. It's the same setting you'll see in Settings.",
	},
	{
		id: "friends-close",
		target: SPOTLIGHT_TARGETS.FRIENDS_CLOSE,
		silent: true,
		interactive: true,
		instruction: "Close the Friends window to continue",
	},
].map((step) => insideModal(ID.SETTINGS, step));

const settingsModalSteps = [
	{
		id: "settings-modal",
		target: SPOTLIGHT_TARGETS.SETTINGS_MODAL,
		title: "Settings",
		body: "Here are the account options you can change at any time.",
	},
	{
		id: "settings-privacy",
		target: SPOTLIGHT_TARGETS.SETTINGS_PRIVACY,
		title: "Account Privacy",
		body: "Private means only approved friends and people with your private link can visit your pages. Turn it off to make your pages public. You'll get a warning first, since a public registry can include your address.",
	},
	{
		id: "settings-tutorials",
		target: SPOTLIGHT_TARGETS.SETTINGS_SHOW_TUTORIALS,
		title: "Show Tutorials",
		body: "This turns the small help dots around MyTassel on or off. It's separate from this guided tour, which only runs as part of your onboarding.",
	},
	{
		id: "settings-close",
		target: SPOTLIGHT_TARGETS.SETTINGS_CLOSE,
		silent: true,
		interactive: true,
		instruction: "Close Settings to continue",
	},
].map((step) => insideModal(ID.VIEW_AS_VISITOR, step));

const pageSettingsModalSteps = [
	{
		id: "page-settings-modal",
		target: SPOTLIGHT_TARGETS.PAGE_SETTINGS_MODAL,
		title: "Page settings",
		body: "This is where you style your announcement page. Here's what you can change.",
	},
	{
		id: "page-settings-background",
		target: SPOTLIGHT_TARGETS.PAGE_SETTINGS_BACKGROUND,
		title: "Background",
		body: "Pick one of the standard backgrounds, or upload your own photo with MyTassel Plus.",
	},
	{
		id: "page-settings-text-color",
		target: SPOTLIGHT_TARGETS.PAGE_SETTINGS_TEXT_COLOR,
		title: "Text color",
		body: "Choose a standard color, or mix a custom one with MyTassel Plus, so your text stands out on your background.",
	},
	{
		id: "page-settings-url",
		target: SPOTLIGHT_TARGETS.PAGE_SETTINGS_URL,
		title: "Personalized URL",
		body: "A shorter, memorable link to share. Your original links keep working either way.",
	},
	{
		id: "page-settings-blur",
		target: SPOTLIGHT_TARGETS.PAGE_SETTINGS_BLUR,
		title: "Background blur",
		body: "Softly blur your background so the content on top is easier to read.",
	},
	{
		id: "page-settings-actions",
		target: SPOTLIGHT_TARGETS.PAGE_SETTINGS_ACTIONS,
		title: "Apply or revert",
		body: "Apply Changes saves what you picked. Revert to Default puts everything back the way it started.",
	},
	{
		id: "page-settings-close",
		target: SPOTLIGHT_TARGETS.PAGE_SETTINGS_MODAL,
		silent: true,
		interactive: true,
		instruction: "Apply your changes or close this window to continue",
	},
].map((step) => insideModal(ID.GRADUATION_CARD, step));

const announcementChapter = {
	id: "announcement",
	title: "My Announcement",
	route: "Announcements",
	routes: ["Announcements", "AnnouncementPage"],
	getRouteParams: ({ userData }) => ({ uid: userData?.uid }),
	// My Announcement opens in a new tab from the Hub, so the Hub tab stays where it is.
	followOnHandoff: false,
	isEligible: ({ userData }) => userData?.accountType === "Graduate",
	next: () => "registry",
	steps: [
		{
			id: "announcement-intro",
			target: SPOTLIGHT_TARGETS.ANNOUNCEMENT_PAGE,
			placement: "center",
			title: "Welcome to My Announcement",
			body: "This is your graduation page. Customize it, share updates, receive messages from friends and family, add widgets, and, eventually, manage your Graduation Fund.",
			actionLabel: "Take the tour",
		},
		{
			id: "go-home",
			target: SPOTLIGHT_TARGETS.HEADER_GO_HOME,
			skipIfTargetMissing: true,
			title: "Go Home",
			body: "This takes you back to your Hub. No need to click it now.",
		},
		{
			id: "logout",
			target: SPOTLIGHT_TARGETS.HEADER_LOGOUT,
			skipIfTargetMissing: true,
			title: "Logout",
			body: "This signs you out of MyTassel.",
		},
		{
			id: ID.FRIENDS,
			target: SPOTLIGHT_TARGETS.HEADER_FRIENDS,
			interactive: true,
			optional: true,
			skipIfTargetMissing: true,
			title: "Friends",
			body: "View and manage your friends and connect with other MyTassel users.",
			instruction: "Click the Friends icon to take a look",
			skipTo: () => ID.SETTINGS,
			waitFor: { event: SPOTLIGHT_EVENTS.HEADER_FRIENDS_OPENED, goto: "friends-modal" },
		},
		...friendsModalSteps,
		{
			id: ID.SETTINGS,
			target: SPOTLIGHT_TARGETS.HEADER_SETTINGS,
			interactive: true,
			optional: true,
			skipIfTargetMissing: true,
			title: "Settings",
			body: "Your account privacy and help options live here.",
			instruction: "Click the Settings icon to open it",
			skipTo: () => ID.VIEW_AS_VISITOR,
			waitFor: { event: SPOTLIGHT_EVENTS.HEADER_SETTINGS_OPENED, goto: "settings-modal" },
		},
		...settingsModalSteps,
		{
			id: ID.VIEW_AS_VISITOR,
			target: SPOTLIGHT_TARGETS.ANNOUNCEMENT_VIEW_AS_VISITOR,
			title: "View as Visitor",
			body: "Preview your page exactly as your guests will see it. What visitors can open depends on your privacy settings. You don't need to click it now.",
		},
		{
			id: ID.EDIT_PAGE_SETTINGS,
			target: SPOTLIGHT_TARGETS.ANNOUNCEMENT_EDIT_PAGE_SETTINGS,
			interactive: true,
			optional: true,
			title: "Edit Page Settings",
			body: "Change how your page looks.",
			instruction: "Click Edit Page Settings to open it",
			skipTo: () => ID.GRADUATION_CARD,
			waitFor: { event: SPOTLIGHT_EVENTS.ANNOUNCEMENT_PAGE_SETTINGS_OPENED, goto: "page-settings-modal" },
		},
		...pageSettingsModalSteps,
		{
			id: ID.GRADUATION_CARD,
			target: SPOTLIGHT_TARGETS.ANNOUNCEMENT_GRADUATION_CARD,
			title: "Your Graduation Card",
			body: "This card introduces you with your name, class year, graduation date and school. You can personalize it:",
			bullets: [
				{ label: "Photos", text: "change the top right and bottom left images." },
				{ label: "Announcement Background & Settings", text: "choose its background and text color." },
			],
		},
		{
			id: "yearbook",
			target: SPOTLIGHT_TARGETS.ANNOUNCEMENT_YEARBOOK,
			title: "Yearbook",
			body: "Friends and family leave messages for you here.",
		},
		{
			id: ID.GRADUATION_FUND,
			target: SPOTLIGHT_TARGETS.ANNOUNCEMENT_FUND_TAB,
			interactive: true,
			optional: true,
			title: "Graduation Fund",
			body: "Let's take a look at your Graduation Fund.",
			instruction: "Click Graduation Fund to open it",
			skipTo: () => ID.REGISTRY_PREVIEW,
			waitFor: { event: SPOTLIGHT_EVENTS.ANNOUNCEMENT_FUND_OPENED, goto: "graduation-fund-section" },
		},
		{
			id: "graduation-fund-section",
			target: SPOTLIGHT_TARGETS.ANNOUNCEMENT_FUND_SECTION,
			allowBack: false,
			title: "Your Graduation Fund",
			body: "Your Graduation Fund lets friends and family chip in to help fund your next chapter. We'll walk you through setting it up later.",
		},
		{
			id: ID.REGISTRY_PREVIEW,
			target: SPOTLIGHT_TARGETS.ANNOUNCEMENT_REGISTRY_TAB,
			allowBack: false,
			title: "Registry",
			body: "This is where your gift registry lives. We'll come back to it at the end of this tour.",
		},
		{
			id: ID.SHARE,
			target: SPOTLIGHT_TARGETS.ANNOUNCEMENT_SHARE_TAB,
			interactive: true,
			optional: true,
			title: "Share",
			body: "Let's see how you can share your page.",
			instruction: "Click Share to open it",
			skipTo: () => WIDGETS_INTRO_STEP_ID,
			waitFor: { event: SPOTLIGHT_EVENTS.ANNOUNCEMENT_SHARE_OPENED, goto: "share-section" },
		},
		{
			id: "share-section",
			target: SPOTLIGHT_TARGETS.ANNOUNCEMENT_SHARE_SECTION,
			allowBack: false,
			title: "Share your page",
			body: "Share your page with friends and family by email, text message (SMS), or QR code. We'll go deeper on sharing later.",
		},
		{
			id: WIDGETS_INTRO_STEP_ID,
			target: [SPOTLIGHT_TARGETS.WIDGET_PICKER, SPOTLIGHT_TARGETS.WIDGET_ADD],
			title: "Widgets",
			body: "Widgets add more content to your page. Try each one, or skip any you don't need. You can always come back later.",
			actionLabel: "Show me the widgets",
		},
		...buildWidgetSteps(WIDGETS_COMPLETE_STEP_ID),
		{
			id: WIDGETS_COMPLETE_STEP_ID,
			target: [SPOTLIGHT_TARGETS.WIDGET_PICKER, SPOTLIGHT_TARGETS.WIDGET_ADD],
			allowBack: false,
			title: "Widgets complete",
			body: "That's every widget. You can add, edit or remove them anytime from this page.",
			actionLabel: "Continue",
		},
		{
			id: ID.REGISTRY,
			target: SPOTLIGHT_TARGETS.ANNOUNCEMENT_REGISTRY_TAB,
			interactive: true,
			allowBack: false,
			title: "Registry",
			body: "This is where you'll set up your gift registry and Graduation Fund. Let's head there next.",
			instruction: "Click Registry to continue",
			waitFor: { event: SPOTLIGHT_EVENTS.ANNOUNCEMENT_REGISTRY_OPENED, goto: COMPLETE_CHAPTER },
		},
	],
};

export default announcementChapter;
